import { type FormEvent, useId, useState } from "react";
import { Mail } from "lucide-react";
import { PageHero } from "@/components/ui/PageHero";
import { buttonClass } from "@/components/ui/buttonStyles";
import { STORE_API_URL } from "@/config/constants";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useAppTranslation } from "@/i18n/useAppTranslation";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** "Mi cuenta" without passwords: the buyer gets every download link again by email. */
export function MyPurchasesPage() {
  usePageSeo("/recursos/mis-compras");
  const { t } = useAppTranslation("resourcesPage");
  const { t: tNav } = useAppTranslation("nav");
  const fieldId = useId();
  const messageId = useId();
  const [state, setState] = useState<"idle" | "sending" | "sent" | "invalid" | "error">("idle");
  const [sentTo, setSentTo] = useState("");

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const email = String(new FormData(e.currentTarget).get("email") ?? "").trim();
    if (!EMAIL.test(email) || email.length > 254) {
      setState("invalid");
      return;
    }
    setState("sending");
    try {
      const res = await fetch(`${STORE_API_URL}/purchases`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error("failed");
      setSentTo(email);
      setState("sent");
    } catch {
      setState("error");
    }
  }

  const invalid = state === "invalid";

  return (
    <main id="main-content" tabIndex={-1} className="outline-none">
      <PageHero
        crumb={t("purchasesCrumb")}
        trail={[{ label: tNav("resources"), to: "/recursos" }]}
        title={t("purchasesTitle")}
        lead={<p>{t("purchasesLead")}</p>}
      />
      <section className="bg-brand-bg">
        <div className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
          <form
            onSubmit={onSubmit}
            noValidate
            className="max-w-xl rounded-[1.75rem] border border-brand-primary/8 bg-white p-7 shadow-lit sm:p-9"
          >
            <label htmlFor={fieldId} className="text-sm font-semibold text-brand-primary">
              {t("email")}
            </label>
            <input
              id={fieldId}
              name="email"
              type="email"
              autoComplete="email"
              required
              aria-invalid={invalid}
              aria-describedby={state !== "idle" && state !== "sending" ? messageId : undefined}
              className="mt-2 w-full rounded-xl border border-brand-primary/15 bg-white px-4 py-3 text-base text-ink outline-none transition-colors focus:border-teal-strong focus:ring-2 focus:ring-brand-secondary/40 aria-[invalid=true]:border-red-700"
            />
            <button
              type="submit"
              disabled={state === "sending"}
              className={buttonClass("primary", "lg", "mt-6 w-full sm:w-auto")}
            >
              <Mail className="size-4" aria-hidden="true" />
              {state === "sending" ? t("purchasesSending") : t("purchasesSubmit")}
            </button>
            {state === "sent" ? (
              <p id={messageId} role="status" className="mt-5 rounded-2xl bg-teal-soft p-4 text-base text-ink-soft">
                {t("purchasesSent", { email: sentTo })}
              </p>
            ) : null}
            {invalid || state === "error" ? (
              <p id={messageId} role="alert" className="mt-4 text-sm text-red-700">
                {invalid ? t("purchasesInvalid") : t("purchasesError")}
              </p>
            ) : null}
          </form>
        </div>
      </section>
    </main>
  );
}
